import type { DefaultError } from "@tanstack/query-core";
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  assignUserToLocation,
  bulkAssignUserToLocations,
  bulkSetScreenerLocationStatus,
  bulkUploadLocations,
  type BulkUploadResponse,
  createScreenerLocation,
  deleteScreenerLocation,
  getAssignedScreenerLocations,
  getScreenerLocationPromptById,
  getScreenerLocationsByScreenerId,
  getTableScreenerLocationsByScreener,
  updateScreenerLocation,
  updateScreenerLocationPrompt,
  updateScreenerLocationStatus,
} from "@/app/api/screener-locations";
import type {
  AssignedLocationsTableQueryParams,
  LocationsTableQueryParams,
} from "@/components/table/views/screener-locations/screener-locations-columns";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import type { FilterParams } from "@/hooks/use-filter-params";
import type {
  ManageLocationsResponse,
  ParsedLocations,
  UpdateLocationMutationVariables,
} from "@/types/manage-locations";
import type {
  AllLocationsResponse,
  CleanLocationDraftForServer,
  IndividualScreenerLocationStatusType,
  PromptData,
  ScreenerLocationsTableResponse,
} from "@/types/screener-location";
import type { Screener } from "@/types/screeners";
import type { DefaultUseQueryConfigParam } from "@/types/third-party";
import type { Nullable } from "@/types/utils";
import { convertLocationToDraft } from "@/utils/manage-locations";

const SCREENER_LOCATIONS_QUERY_KEY_BASE = "screener_location" as const;
const TABLE_KEY = "table" as const;
const ASSIGNED = "assigned" as const;
export const SCREENER_LOCATION_QUERY_KEYS = {
  base: [SCREENER_LOCATIONS_QUERY_KEY_BASE] as const,
  // When filters is omitted, returns a partial key for invalidation
  tableDataByScreenerId: (screenerId: string, filters?: object) =>
    filters
      ? ([
          ...SCREENER_LOCATION_QUERY_KEYS.base,
          TABLE_KEY,
          screenerId,
          filters,
        ] as const)
      : ([
          ...SCREENER_LOCATION_QUERY_KEYS.base,
          TABLE_KEY,
          screenerId,
        ] as const),
  byScreenerId: (screenerId: string) => [
    ...SCREENER_LOCATION_QUERY_KEYS.base,
    screenerId,
  ],
  // When filters is omitted, returns a partial key for invalidation
  assigned: (filters?: object) =>
    filters
      ? ([...SCREENER_LOCATION_QUERY_KEYS.base, ASSIGNED, filters] as const)
      : ([...SCREENER_LOCATION_QUERY_KEYS.base, ASSIGNED] as const),
  promptByLocationId: (locationId: string) => [
    ...SCREENER_LOCATION_QUERY_KEYS.base,
    "prompt",
    locationId,
  ],
};
export const SCREENER_LOCATIONS_MUTATION_KEYS = {
  updateScreenerLocation: () => [
    ...SCREENER_LOCATION_QUERY_KEYS.base,
    "updateScreenerLocation",
  ],
};

//////////////////////////////////////
//// Screener Location Table Data ////
//////////////////////////////////////

export const useTableScreenerLocationsByScreenerQuery = (
  screenerId: string,
  filterParams: FilterParams<LocationsTableQueryParams>,
  config: DefaultUseQueryConfigParam<AllLocationsResponse> = {},
) => {
  return useAuthedQuery({
    placeholderData: (previousData) => previousData,
    queryKey: SCREENER_LOCATION_QUERY_KEYS.tableDataByScreenerId(
      screenerId,
      filterParams.toObject(),
    ),
    queryFn: () =>
      getTableScreenerLocationsByScreener(screenerId, filterParams.toString()),
    ...config,
  });
};

export const useAssignedScreenerLocationsQuery = (
  filterParams: FilterParams<AssignedLocationsTableQueryParams>,
  config: DefaultUseQueryConfigParam<ScreenerLocationsTableResponse> = {},
) => {
  return useAuthedQuery({
    placeholderData: (previousData) => previousData,
    queryKey: SCREENER_LOCATION_QUERY_KEYS.assigned(filterParams.toObject()),
    queryFn: () => getAssignedScreenerLocations(filterParams.toString()),
    ...config,
  });
};

export const useAssignUserToLocationMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId,
      userId,
    }: {
      locationId: string;
      userId: Nullable<string>;
    }) => assignUserToLocation(locationId, userId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base });
    },
  });
};

export const useBulkAssignUserToLocationsMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationIds,
      userId,
    }: {
      locationIds: string[];
      userId: Nullable<string>;
    }) => bulkAssignUserToLocations(locationIds, userId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base });
    },
  });
};

type UpdateStatusPayload = {
  locationId: string;
  status: IndividualScreenerLocationStatusType;
};

export const useUpdateScreenerLocationStatusMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, status }: UpdateStatusPayload) =>
      updateScreenerLocationStatus(locationId, status),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base });
    },
  });
};

///////////////////////////////////
//// Manage Screener Locations ////
///////////////////////////////////

export const useScreenerLocationByScreenerIdQuery = (
  screenerId: string,
  // More complex types than normal here because we are fetching from a function
  // that returns the ScreenerLocation type, but then we transform to the
  // ScreenerLocationDraft type in `select`
  config: Partial<
    UseQueryOptions<
      ManageLocationsResponse,
      DefaultError,
      { parsedLocations: ParsedLocations; screener: Screener }
    >
  > = {},
) => {
  return useAuthedQuery<
    ManageLocationsResponse,
    DefaultError,
    { parsedLocations: ParsedLocations; screener: Screener }
  >({
    queryKey: SCREENER_LOCATION_QUERY_KEYS.byScreenerId(screenerId),
    queryFn: () => getScreenerLocationsByScreenerId(screenerId),
    select: ({ locations, screener }) => ({
      parsedLocations: {
        drafts: locations.map(convertLocationToDraft),
        locations,
      },
      screener,
    }),
    ...config,
  });
};

export const useUpdateScreenerLocationMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId,
      screenerLocation,
    }: UpdateLocationMutationVariables) =>
      updateScreenerLocation(locationId, screenerLocation),
    mutationKey: SCREENER_LOCATIONS_MUTATION_KEYS.updateScreenerLocation(),
    onSettled: () =>
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base }),
  });
};

export const useCreateScreenerLocationMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      newLocation,
    }: {
      newLocation: CleanLocationDraftForServer;
    }) => createScreenerLocation(newLocation, screenerId),
    onSuccess: (newLocation) => {
      client.invalidateQueries({
        queryKey:
          SCREENER_LOCATION_QUERY_KEYS.tableDataByScreenerId(screenerId),
      });

      client.setQueryData(
        SCREENER_LOCATION_QUERY_KEYS.byScreenerId(screenerId),
        ({ locations, screener }: ManageLocationsResponse) => ({
          screener,
          locations: [newLocation, ...locations],
        }),
      );
    },
  });
};

export const useBulkSetScreenerLocationStatusMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationIds,
      status,
    }: {
      locationIds: string[];
      status: IndividualScreenerLocationStatusType;
    }) => bulkSetScreenerLocationStatus(locationIds, status),
    onSuccess: () => {
      return client.invalidateQueries({
        queryKey: SCREENER_LOCATION_QUERY_KEYS.base,
      });
    },
  });
};

export const useDeleteScreenerLocationMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId }: { locationId: string }) =>
      deleteScreenerLocation(locationId),
    onSuccess: (locations, { locationId }) => {
      client.setQueryData(
        SCREENER_LOCATION_QUERY_KEYS.byScreenerId(screenerId),
        (oldLocations: ManageLocationsResponse) => ({
          locations: oldLocations.locations.filter(
            ({ id }) => id !== locationId,
          ),
          screener: oldLocations.screener,
        }),
      );
    },
  });
};

export const useGetScreenerLocationPromptByIdQuery = (
  locationId: string,
  options?: Partial<UseQueryOptions<PromptData>>,
) => {
  return useAuthedQuery<PromptData>({
    queryKey: SCREENER_LOCATION_QUERY_KEYS.promptByLocationId(locationId),
    queryFn: () => {
      return getScreenerLocationPromptById(locationId);
    },
    ...options,
  });
};

export const useScreenerLocationPromptMutation = (locationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      promptId,
      updatedPromptText,
    }: {
      promptId: string;
      updatedPromptText: string;
    }) => updateScreenerLocationPrompt(promptId, updatedPromptText),
    onSuccess({ id, synthesizedPromptText }) {
      queryClient.setQueryData(
        SCREENER_LOCATION_QUERY_KEYS.promptByLocationId(locationId),
        { screenerLocationPromptId: id, synthesizedPromptText },
      );
    },
  });
};

export const useBulkUploadLocationsMutation = (
  screenerId: string,
): UseMutationResult<BulkUploadResponse, Error, File> => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => bulkUploadLocations(screenerId, file),
    onSuccess: () => {
      // Invalidate all location queries to refresh the list
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base });
    },
  });
};
